import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Brain,
  Users,
  ListOrdered,
  Flame,
  Settings,
  Shield,
  Crown,
  Lock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  LogOut,
  Key,
  Eye,
  EyeOff,
} from "lucide-react";
import { getDatabase, ref, onValue, off, get } from "../../Config/matuRtdb";
import { getAuth, onAuthStateChanged, signOut } from "../../Config/matuAuth";
import BetVip from "./BetVip";
import SocialWorld from "../World/SocialWorld";
import Marcadores from "../Home/Marcadores";
import ChatIA from "./ChatIA";
import Combi from "./Combi";
import { BsTelephone, BsWhatsapp } from "react-icons/bs";
import { FaBlog } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { VipRouteGuard, VipContent } from './VipRouteGuard';
import SecurityMonitor from './SecurityMonitor';
import ConfigModal from "./ConfigModal";

const tabs = [
  { id: "Inicio", label: "Inicio", icon: Home, color: "text-blue-500", requiresVip: true },
  { id: "Feed", label: "Feed", icon: Users, color: "text-green-500", requiresVip: true },
  { id: "Parlay", label: "Parlay", icon: Flame, color: "text-red-500", requiresVip: true },
  {
    id: "Marcador",
    label: "Marcador",
    icon: ListOrdered,
    color: "text-yellow-500",
    requiresVip: true,
  },
  { id: "IA", label: "Asistente", icon: Brain, color: "text-purple-500", requiresVip: true },
];

export default function HomeVip() {
  const [activeTab, setActiveTab] = useState("Inicio");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

  // Estados de autenticación y autorización
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isVipUser, setIsVipUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [showVipModal, setShowVipModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getDatabase();

  // Verificar estado de autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        await checkUserVipStatus(user.uid);
      } else {
        setUser(null);
        setUserProfile(null);
        setIsVipUser(false);
        navigate("/prediccion-zone/mi-zona"); // Redirigir al Home principal
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  // Verificar estado VIP del usuario
  const checkUserVipStatus = async (userId) => {
    try {
      const userRef = ref(db, `usuarios/${userId}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        setUserProfile(userData);
        
        // Verificar si es usuario VIP usando el campo typeU
        const isVip = userData.typeU === "Cuenta Vip";
        
        setIsVipUser(isVip);
        
        // Si no es VIP y está en pestaña VIP, redirigir
        if (!isVip && tabs.find(tab => tab.id === activeTab)?.requiresVip) {
          navigate("/"); // Redirigir al Home principal
          setShowVipModal(true);
        }
      } else {
        setIsVipUser(false);
      }
    } catch (error) {
      console.error("Error verificando estado VIP:", error);
      setIsVipUser(false);
    }
  };

  // Función de login
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setAuthError(null);

    try {
      // Aquí implementarías la lógica de login
      // Por ahora simulamos un login exitoso
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Verificar credenciales (esto debería conectarse con tu sistema de auth)
      if (loginCredentials.email && loginCredentials.password) {
        setShowLoginModal(false);
        setLoginCredentials({ email: "", password: "" });
        // Recargar estado del usuario
        if (user) {
          await checkUserVipStatus(user.uid);
        }
      } else {
        setAuthError("Credenciales inválidas");
      }
    } catch (error) {
      setAuthError("Error al iniciar sesión");
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Función de logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  // Verificar acceso a pestaña VIP
  const handleTabChange = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    
    if (tab?.requiresVip && !isVipUser) {
      setShowVipModal(true);
      return;
    }
    
    setActiveTab(tabId);
  };

  // Renderizado condicional basado en la pestaña activa
  const renderTabContent = () => {
    // Verificar acceso VIP para pestañas restringidas
    const currentTab = tabs.find(tab => tab.id === activeTab);
    
    if (currentTab?.requiresVip && !isVipUser) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
          <Lock className="w-16 h-16 text-yellow-500 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Contenido VIP Bloqueado</h2>
          <p className="text-gray-400 mb-6 max-w-md">
            Esta sección es exclusiva para usuarios VIP. Actualiza tu cuenta para acceder a contenido premium.
          </p>
          <button
            onClick={() => setShowVipModal(true)}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            <Crown className="w-5 h-5 inline mr-2" />
            Actualizar a VIP
          </button>
        </div>
      );
    }

    switch (activeTab) {
      case "Inicio":
        return <BetVip />;
      case "Feed":
        return <SocialWorld />;
      case "Marcador":
        return <Marcadores />;
      case "Parlay":
        return <Combi />;
      case "IA":
        return <ChatIA />;
      default:
        return <BetVip />;
    }
  };

  // Función para manejar el clic en el icono flotante de Flame
  const handleFlameClick = () => {
    if (isVipUser) {
      handleTabChange("Parlay");
    } else {
      setShowVipModal(true);
    }
  };

  // Manejar notificaciones
  const handleNotificationClick = () => {
    window.location.href = "https://wa.me/573023580862?text=Hey!%20Pro";
  };

  const handleBlogLink = () => {
    window.location.href = "https://deportivosblogs.online/";
  };

  const handleTel = () => {
    window.location.href = "tel:3023580862";
  };

  // Función para abrir modal de configuración
  const handleSettingsClick = () => {
    if (user) {
    setIsConfigModalOpen(true);
    } else {
      setShowLoginModal(true);
    }
  };

  // Función para cerrar modal de configuración
  const handleCloseConfigModal = () => {
    setIsConfigModalOpen(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-blue-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Verificando acceso VIP...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col w-full bg-gradient-to-b from-gray-900 to-blue-900 text-white">
      {/* Header mejorado con información del usuario */}
      <header className="p-3 flex justify-between items-center bg-gradient-to-r from-blue-900/80 to-purple-900/80 backdrop-blur-sm border-b border-blue-700/50">
        <div className="flex items-center space-x-3">
          <div className="text-xl font-bold flex items-center">
            <Crown className="w-6 h-6 mr-2 text-yellow-400" />
            VIP🔥
          </div>
          {user && (
            <div className="flex items-center space-x-2 text-sm">
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                isVipUser 
                  ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' 
                  : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
              }`}>
                {isVipUser ? (
                  <>
                    <Crown className="w-3 h-3 inline mr-1" />
                    VIP Activo
                  </>
                ) : (
                  <>
                    <User className="w-3 h-3 inline mr-1" />
                    Usuario Free
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3">
          {user ? (
            <>
              <button onClick={handleNotificationClick} className="hover:scale-110 transition-transform duration-200">
                <BsWhatsapp size={20} className="text-gray-300 hover:text-green-400" />
              </button>
              <button onClick={handleTel} className="hover:scale-110 transition-transform duration-200">
                <BsTelephone size={20} className="text-gray-300 hover:text-blue-400" />
              </button>
              {/* <button onClick={handleBlogLink} className="hover:scale-110 transition-transform duration-200">
                <FaBlog size={20} className="text-gray-300 hover:text-purple-400" />
              </button> */}
              <button onClick={handleSettingsClick} className="hover:scale-110 transition-transform duration-200">
                <Settings size={20} className="text-gray-300 hover:text-white" />
              </button>
              <button 
                onClick={handleLogout}
                className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
              >
                <LogOut size={16} />
                <span>Salir</span>
              </button>
            </>
          ) : (
            <>
              <button onClick={handleNotificationClick} className="hover:scale-110 transition-transform duration-200">
                <BsWhatsapp size={20} className="text-gray-300 hover:text-green-400" />
              </button>
              <button onClick={handleTel} className="hover:scale-110 transition-transform duration-200">
                <BsTelephone size={20} className="text-gray-300 hover:text-blue-400" />
            </button>
              <button onClick={handleBlogLink} className="hover:scale-110 transition-transform duration-200">
                <FaBlog size={20} className="text-gray-300 hover:text-purple-400" />
            </button>
              <button 
                onClick={() => setShowLoginModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors duration-200 flex items-center space-x-2"
              >
                <Key size={16} />
                <span>Iniciar Sesión</span>
            </button>
          </>
          )}
        </div>
      </header>

      {/* Monitor de Seguridad */}
      {/* <SecurityMonitor showDetails={isVipUser} /> */}
      
      {/* Banner de estado VIP */}
      {user && !isVipUser && (
        <div className="mx-3 mt-3 p-4 bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-500/30 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
              <div>
                <h3 className="text-white font-semibold">Actualiza a VIP</h3>
                <p className="text-yellow-200 text-sm">Todo el contenido VIP es exclusivo para usuarios premium</p>
              </div>
            </div>
            <button
              onClick={() => setShowVipModal(true)}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105"
            >
              <Crown className="w-4 h-4 inline mr-2" />
              Actualizar
            </button>
          </div>
        </div>
      )}

      {/* Contenedor principal con animación */}
      <AnimatePresence mode="wait">
        <motion.main
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="flex-grow w-full p-3 mb-24"
        >
          {renderTabContent()}
        </motion.main>
      </AnimatePresence>

      {/* Barra de navegación mejorada */}
      <nav className="fixed bottom-0 left-0 w-full bg-gradient-to-r from-blue-900 to-blue-700 shadow-lg rounded-t-xl z-50">
        <div className="flex justify-around p-2">
          {tabs.map(({ id, label, icon: Icon, color, requiresVip }) => {
            const isDisabled = requiresVip && !isVipUser;
            const isActive = activeTab === id;
            
            return (
            <button
              key={id}
                onClick={() => handleTabChange(id)}
                disabled={isDisabled}
                className={`relative flex flex-col items-center transition-all duration-300 py-1 ${
                  isDisabled 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:opacity-75 cursor-pointer'
                }`}
                title={isDisabled ? 'Sección VIP - Actualiza tu cuenta' : label}
              >
                <div className={`p-2 rounded-full relative ${
                  isActive ? `bg-opacity-20 bg-white ${color}` : ""
                }`}>
                <Icon
                  size={20}
                    className={isActive ? color : isDisabled ? "text-gray-500" : "text-gray-300"}
                />
                  {requiresVip && !isVipUser && (
                    <Lock className="w-3 h-3 absolute -top-1 -right-1 text-yellow-400" />
                  )}
              </div>
              <span
                className={`text-xs mt-1 ${
                    isActive 
                      ? `font-medium ${color}` 
                      : isDisabled 
                        ? "text-gray-500" 
                        : "text-gray-300"
                }`}
              >
                {label}
              </span>
                {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute -bottom-2 h-1 w-8 bg-white rounded-full"
                />
              )}
            </button>
            );
          })}
        </div>
      </nav>

      {/* Botón flotante VIP */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{
          opacity: 1,
          scale: 1,
          y: [0, -10, 0],
          transition: { y: { repeat: Infinity, duration: 2 } },
        }}
        onClick={handleFlameClick}
        className={`fixed bottom-24 right-4 rounded-full p-3 shadow-lg cursor-pointer z-40 transition-all duration-300 ${
          isVipUser 
            ? 'bg-gradient-to-r from-yellow-500 to-red-500 hover:from-yellow-600 hover:to-red-600' 
            : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
        }`}
        title={isVipUser ? 'Acceso VIP - Parlay' : 'Actualiza a VIP para acceder'}
      >
        <Flame size={24} className="text-white" />
        {!isVipUser && (
          <Lock className="w-4 h-4 absolute -top-1 -right-1 text-yellow-400" />
        )}
      </motion.div>

      {/* Modal de configuración */}
      <ConfigModal
        isOpen={isConfigModalOpen}
        onClose={handleCloseConfigModal}
      />

      {/* Modal de actualización VIP */}
      {showVipModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 max-w-md w-full border border-gray-700"
          >
            <div className="text-center mb-6">
              <div className="bg-yellow-500/20 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Crown className="w-8 h-8 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Actualiza a VIP</h2>
              <p className="text-gray-400">
                Accede a contenido exclusivo, pronósticos premium y herramientas avanzadas
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center space-x-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Pronósticos VIP exclusivos</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Acceso a Parlay y IA</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Soporte prioritario</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Contenido premium</span>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowVipModal(false);
                  navigate("/"); // Ir al Home principal
                }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
              >
                Ir al Home
              </button>
              <button
                onClick={() => {
                  setShowVipModal(false);
                  window.location.href = "https://wa.me/573023580862?text=Hola!%20Quiero%20actualizar%20a%20VIP";
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg font-medium transition-all duration-300 transform hover:scale-105"
              >
                <Crown className="w-4 h-4 inline mr-2" />
                Contactar
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal de login */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 max-w-md w-full border border-gray-700"
          >
            <div className="text-center mb-6">
              <div className="bg-blue-500/20 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Key className="w-8 h-8 text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Iniciar Sesión</h2>
              <p className="text-gray-400">
                Accede a tu cuenta para continuar
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={loginCredentials.email}
                  onChange={(e) => setLoginCredentials({...loginCredentials, email: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="tu@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={loginCredentials.password}
                    onChange={(e) => setLoginCredentials({...loginCredentials, password: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {authError && (
                <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center space-x-2">
                  <XCircle className="w-4 h-4" />
                  <span>{authError}</span>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowLoginModal(false);
                    navigate("/"); // Ir al Home principal
                  }}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
                >
                  Ir al Home
                </button>
                <button
                  type="submit"
                  disabled={isLoggingIn}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
                >
                  {isLoggingIn ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Conectando...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      Conectar
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
